import './date-picker.styles.css'
import { Box, Flex, FormLabel, type FormLabelProps } from '@chakra-ui/react'
import { de } from 'date-fns/locale'
import { type ReactElement, useCallback, useRef, useState } from 'react'
import ReactDatePicker, { registerLocale } from 'react-datepicker'
import { useTranslation } from 'react-i18next'
import { BaseTooltip } from '../Tooltip'
import { DatePickerHeader } from './DatePickerHeader'
import { DatePickerInput } from './DatePickerInput'
import { DatePickerPresets } from './DatePickerPresets'
import { DatePickerTimeSelector } from './DatePickerTimeSelector'
import { filterPresetsWithinRange } from './datePicker.presets'
import type { DateRangePreset, DateRangePresetValue } from './datePicker.presets'
import type { DatePickerType } from './datePicker.types'
import { useDatePicker } from './useDatePicker.hook'

registerLocale('de', de)

interface BaseDatePickerProps {
  pickerType: DatePickerType
  isDisabled?: boolean
  leftIcon?: React.ReactNode
  minDate?: Date | string
  maxDate?: Date | string
  excludeDates?: Date[]
  filterDate?: (date: Date) => boolean
  disabledYears?: number[]
  name?: string
  width?: string
  fontWeight?: string
  monthsShown?: number
  size?: 'small' | 'medium'
  height?: string
  placeholderText?: string
  allowSameDateSelection?: boolean
  popperPlacement?: any
  portalId?: string
  presets?: DateRangePreset[]
  /** Called when the user picks one of the presets, after onChange received its dates */
  onPresetSelect?: (preset: DateRangePreset) => void
  label?: string
  labelProps?: FormLabelProps
  containerStyle?: React.CSSProperties
  infoTooltip?: string
  /** Opens the day calendar as soon as the control mounts (single-date pickers). */
  startWithCalendarOpen?: boolean
  /**
   * Replaces the default text field. The calendar popper anchors to this node (e.g. pill button).
   * Must be an element whose component uses `forwardRef` to a focusable element.
   */
  customInput?: ReactElement
}

interface SingleDatePickerProps extends BaseDatePickerProps {
  isRange?: false
  value?: string | Date | null | undefined
  initialValue?: string | Date | null | undefined
  onChange?: (date: Date | null) => void
  shouldStoreState?: false
}

interface RangeDatePickerProps extends BaseDatePickerProps {
  isRange: true
  value?: [string | Date | null | undefined, string | Date | null | undefined]
  initialValue?: [string | Date | null | undefined, string | Date | null | undefined]
  onChange?: (dateRange: [Date | null, Date | null]) => void
  shouldStoreState?: boolean
}

export type DatePickerProps = RangeDatePickerProps | SingleDatePickerProps

export function DatePicker(props: DatePickerProps) {
  const { t } = useTranslation()
  const datePickerRef = useRef<any>(null)
  const [selectedPreset, setSelectedPreset] = useState<DateRangePresetValue | undefined>(undefined)
  const weekPrefix = t('iso_suite.enpi.calendar_week_prefix')

  const {
    isOpen,
    setIsOpen,
    startDate,
    endDate,
    minDate,
    maxDate,
    monthsShown,
    isRange,
    showYearPicker,
    showMonthYearPicker,
    showQuarterYearPicker,
    dateFormat,
    timeFormat,
    showTimeSelect,
    selectedTime,
    isFinalSelection,
    popperModifiers,
    handleCalendarClose,
    handleTimeSelect,
    handleDateTimeConfirm,
    handleManualInput,
    handleChange,
    handleKeyPress,
  } = useDatePicker({
    pickerType: props.pickerType,
    value: props.value as any,
    initialValue: props.initialValue as any,
    onChange: props.onChange as any,
    isRange: props.isRange,
    monthsShown: props.monthsShown,
    minDate: props.minDate,
    maxDate: props.maxDate,
    datePickerRef,
    shouldStoreState: props.shouldStoreState,
    allowSameDateSelection: props.allowSameDateSelection,
    weekPrefix,
    startWithCalendarOpen: props.startWithCalendarOpen,
  })

  const handlePresetSelect = useCallback(
    (preset: DateRangePreset) => {
      const [presetStart, presetEnd] = preset.getDateRange()
      setSelectedPreset(preset.value)

      if (props.isRange) {
        handleChange([presetStart, presetEnd])
      }
      props.onPresetSelect?.(preset)
      setIsOpen(false)
    },
    [props.isRange, props.onPresetSelect, handleChange]
  )

  const modifiedMinDate = props.pickerType === 'datetime' && isFinalSelection && isRange && startDate ? startDate : minDate
  const selected = props.pickerType === 'datetime' ? (isRange ? (isFinalSelection ? endDate : startDate) : startDate) : startDate
  const filteredPresets = props.presets ? filterPresetsWithinRange(props.presets, minDate, maxDate) : []
  const hasPresets = filteredPresets.length > 0

  return (
    <Box
      className='date-picker-container'
      style={{
        ...props.containerStyle,
        ...(props.customInput ? { display: 'inline-block', verticalAlign: 'middle' as const } : {}),
      }}
    >
      {props.label && (
        <Flex justifyContent='flex-start' marginBottom={2} alignItems={'center'}>
          <FormLabel fontSize={'12px'} marginBottom={0} color={'#656565'} fontWeight={400} mr={'4px'} {...props.labelProps}>
            {props.label}
          </FormLabel>
          {props.infoTooltip && <BaseTooltip blackIcon width={'16px'} height={'16px'} label={props.infoTooltip} />}
        </Flex>
      )}

      <ReactDatePicker
        onInputClick={() => {
          if (!isOpen) setIsOpen(true)
        }}
        onClickOutside={() => {
          setIsOpen(false)
          setSelectedPreset(undefined)
        }}
        open={isOpen}
        ref={datePickerRef}
        name={props.name}
        selectsMultiple={undefined}
        swapRange={true}
        disabled={props.isDisabled}
        disabledKeyboardNavigation
        locale={'de'}
        portalId={props.portalId}
        popperPlacement={props.popperPlacement ?? 'bottom-start'}
        showWeekNumbers={props.pickerType === 'week'}
        selected={selected}
        selectsRange={isRange as any}
        startDate={isRange ? startDate ?? undefined : undefined}
        endDate={isRange ? endDate ?? undefined : undefined}
        minDate={modifiedMinDate}
        maxDate={maxDate}
        excludeDates={props.excludeDates}
        filterDate={props.filterDate}
        monthsShown={monthsShown}
        // Always renders 6 week rows, padded with adjacent months' days, so the calendar's
        // height (and therefore the position of the month/year nav) stays constant across
        // months instead of shrinking/growing with the number of weeks in the current month.
        fixedHeight
        dateFormat={dateFormat}
        timeFormat={timeFormat}
        showYearPicker={showYearPicker}
        onKeyDown={handleKeyPress}
        showMonthYearPicker={showMonthYearPicker}
        showQuarterYearPicker={showQuarterYearPicker}
        showTimeSelect={showTimeSelect}
        shouldCloseOnSelect={false}
        onChangeRaw={handleManualInput}
        onChange={date => {
          handleChange(date)
          // Clear selected preset when manually changing dates
          if (selectedPreset) {
            setSelectedPreset(undefined)
          }
        }}
        focusSelectedMonth={false}
        onCalendarClose={handleCalendarClose}
        formatWeekDay={formattedDate => formattedDate.substring(0, 1)}
        placeholderText={props.placeholderText ?? 'abc'}
        renderCustomHeader={headerProps => (
          <DatePickerHeader
            {...headerProps}
            pickerType={props.pickerType}
            minDate={minDate}
            maxDate={maxDate}
            disabledYears={props.disabledYears}
          />
        )}
        customInput={
          props.customInput ?? (
            <DatePickerInput
              pickerType={props.pickerType}
              isRange={props.isRange}
              isDisabled={props.isDisabled}
              leftIcon={props.leftIcon}
              width={props.width}
              size={props.size}
              fontWeight={props.fontWeight}
              height={props.height ?? '32px'}
              onKeyDown={handleKeyPress}
            />
          )
        }
        showPopperArrow={false}
        popperModifiers={popperModifiers}
        calendarClassName={`${props.pickerType}-picker ${hasPresets ? 'with-presets' : ''}`}
      >
        {hasPresets && (
          <DatePickerPresets
            presets={filteredPresets}
            currentRange={[startDate, endDate]}
            onPresetSelect={handlePresetSelect}
            selectedPreset={selectedPreset}
          />
        )}
        {props.pickerType === 'datetime' && (
          <DatePickerTimeSelector
            selectedTime={selectedTime}
            startDate={startDate}
            isFinalSelection={isFinalSelection}
            selectedDate={selected}
            minDate={modifiedMinDate}
            maxDate={maxDate}
            onTimeSelect={handleTimeSelect}
            onConfirm={() => {
              handleDateTimeConfirm()
              if (isFinalSelection) {
                datePickerRef.current?.setOpen(false)
              }
            }}
            isRange={isRange}
          />
        )}
      </ReactDatePicker>
    </Box>
  )
}
